import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { Search } from "lucide-react";
import { LimitCardGrid } from "@/features/subventions/components/feed/LimitCardGrid";
import { ReferenceList } from "@/features/subventions/components/feed/ReferenceList";
import { SectionHeading } from "@/features/subventions/components/feed/SectionHeading";
import { SubventionUsageHistory } from "@/features/subventions/components/feed/SubventionUsageHistory";
import { UniversePills } from "@/features/subventions/components/feed/UniversePills";
import type { EligibleSubsidy } from "@/features/subventions/types/subvention.types";
import {
  bindingGauge, displayName, envelopeName, eur, formatDateShort, limitCards, qty,
  remainingFigure, resetDate, smallestCap, type StatusTone, statusOf, stripLeadingEmoji, tileFor,
} from "@/features/subventions/utils/subventionStatus";
import { universeIcon } from "@/features/subventions/utils/universeActions";
import { ResponsiveDialog } from "@/shared/components/responsiveDialog";

type SubventionDetailDrawerProps = {
  subsidy: EligibleSubsidy | null
  holderLabel: string | null
  holderId: string | number | null
  onClose: () => void
};

const MANY_REFERENCES_THRESHOLD = 6;

const BAR_TONE: Record<StatusTone, string> = {
  ok: "bg-success",
  warn: "bg-warning",
  depleted: "bg-error",
};

const heroUnit = (subsidy: EligibleSubsidy): string => {
  const figure = remainingFigure(subsidy);
  const cap = smallestCap(subsidy);
  const secondary = figure.secondary ? `${figure.secondary} · ` : "";
  if (!cap) return `${secondary}disponibles`;
  const capText = cap.money ? eur(cap.value) : qty(cap.value);
  return `${secondary}sur ${capText} ${cap.periodFr}`;
};

const heroReset = (subsidy: EligibleSubsidy): string | null => {
  const reset = resetDate(subsidy);
  if (reset) return `Se réinitialise le ${formatDateShort(reset)}`;
  const status = statusOf(subsidy);
  if (!status.date) return null;
  if (status.origin === "reset") return `Se réinitialise le ${formatDateShort(status.date)}`;
  return status.expired ? `Expiré le ${formatDateShort(status.date)}` : `Jusqu'au ${formatDateShort(status.date)}`;
};

const drawerDescription = (subsidy: EligibleSubsidy, holderLabel: string | null): string | undefined => {
  const lines: string[] = [];
  const env = envelopeName(subsidy);
  if (env) lines.push(`Enveloppe ${stripLeadingEmoji(env)}`);
  if (holderLabel) lines.push(holderLabel);
  return lines.length ? lines.join(" — ") : undefined;
};

export const SubventionDetailDrawer = ({
  subsidy, holderLabel, holderId, onClose,
}: SubventionDetailDrawerProps) => {
  const [search, setSearch] = useState("");
  // Keep the last subsidy while the drawer animates closed, and reset the search per subsidy.
  const [rendered, setRendered] = useState<EligibleSubsidy | null>(subsidy);

  useEffect(() => {
    if (subsidy) {
      setRendered(subsidy);
      setSearch("");
    }
  }, [subsidy]);

  if (!rendered) {
    return <ResponsiveDialog open={false} onClose={onClose} title="Détail de la subvention" variant="drawer">{null}</ResponsiveDialog>;
  }
  return (
    <DrawerContent
      subsidy={rendered}
      open={subsidy !== null}
      holderLabel={holderLabel}
      holderId={holderId}
      onClose={onClose}
      search={search}
      setSearch={setSearch}
    />
  );
};

// Periwinkle hero banner (white-on-brand), matching the mockup: tile, title, envelope/holder line,
// the figure + unit, the reset/expiry line, and the single binding-cap bar.
const DrawerHero = ({ subsidy, subtitle }: { subsidy: EligibleSubsidy, subtitle: string | undefined }) => {
  const figure = remainingFigure(subsidy);
  const showHero = subsidy.display_limit_label && figure.primary;
  const gauge = bindingGauge(subsidy);
  const reset = heroReset(subsidy);

  return (
    <div className="bg-primary px-6 pb-5 pt-6 text-white">
      <div className="flex items-start gap-3 pr-10">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20 text-[23px]" aria-hidden>
          {tileFor(subsidy, universeIcon)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[16.5px] font-extrabold leading-tight">{displayName(subsidy)}</p>
          {subtitle && <p className="mt-0.5 text-[12.5px] font-medium text-white/95" aria-hidden>{subtitle}</p>}
        </div>
      </div>
      {showHero && (
        <div className="mt-3.5 flex flex-wrap items-baseline gap-2.5">
          <span className="text-[32px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">{figure.primary}</span>
          <span className="text-[13px] font-medium text-white/95">{heroUnit(subsidy)}</span>
          {reset && <span className="w-full text-[12px] font-medium text-white/95">{reset}</span>}
        </div>
      )}
      {showHero && gauge && (
        <div className="mt-3 h-[7px] overflow-hidden rounded-full bg-black/25" aria-hidden>
          <span className={clsx("block h-full rounded-full", BAR_TONE[gauge.tone])} style={{ width: `${gauge.pct}%` }} />
        </div>
      )}
    </div>
  );
};

DrawerHero.displayName = "DrawerHero";

type DrawerReferencesProps = {
  subsidy: EligibleSubsidy
  search: string
  setSearch: (value: string) => void
};

// The "comment c'est calculé" / enseignes block. Long perimeters (63 refs) get a search box with
// a live result count, so a non-matching query is never a silent dead end.
const DrawerReferences = ({ subsidy, search, setSearch }: DrawerReferencesProps) => {
  const references = subsidy.references ?? [];
  if (references.length === 0) return null;

  const manyReferences = references.length > MANY_REFERENCES_THRESHOLD;
  const filtered = manyReferences && search
    ? references.filter((r) => (r.name ?? "").toLowerCase().includes(search.toLowerCase()))
    : references;
  const heading = references.length > 1 ? `${references.length} enseignes / règles` : "Comment c'est calculé";

  return (
    <section className="mt-1">
      <SectionHeading>{heading}</SectionHeading>
      {manyReferences && (
        <>
          <p className="mb-2 text-[11px] text-gray-400 dark:text-gray-500">
            {references.length}
            {" enseignes éligibles."}
          </p>
          <div className="relative mb-2.5">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filtrer les enseignes…"
              aria-label="Filtrer les enseignes"
              className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-[12.5px] text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            />
          </div>
        </>
      )}
      {manyReferences && search && (
        <p role="status" aria-live="polite" className="mb-2 text-[11px] text-gray-400 dark:text-gray-500">
          {filtered.length === 0
            ? "Aucune enseigne ne correspond à votre recherche."
            : `${filtered.length} enseigne${filtered.length > 1 ? "s" : ""} sur ${references.length}`}
        </p>
      )}
      <div className={manyReferences ? "max-h-[320px] overflow-y-auto pr-1" : ""}>
        <ReferenceList references={filtered} variant="drawer" />
      </div>
    </section>
  );
};

DrawerReferences.displayName = "DrawerReferences";

type DrawerContentProps = {
  subsidy: EligibleSubsidy
  open: boolean
  holderLabel: string | null
  holderId: string | number | null
  onClose: () => void
  search: string
  setSearch: (value: string) => void
};

const DrawerContent = ({
  subsidy, open, holderLabel, holderId, onClose, search, setSearch,
}: DrawerContentProps) => {
  const comments: string[] = [];
  if (subsidy.perimeter_comment_enabled && subsidy.perimeter_comment) comments.push(subsidy.perimeter_comment);
  if (subsidy.global_comment_enabled && subsidy.global_comment && subsidy.global_comment !== subsidy.perimeter_comment) {
    comments.push(subsidy.global_comment);
  }

  const cards = limitCards(subsidy);
  const subtitle = drawerDescription(subsidy, holderLabel);

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      variant="drawer"
      title={displayName(subsidy)}
      description={subtitle}
      hero={<DrawerHero subsidy={subsidy} subtitle={subtitle} />}
    >
      <div className="flex flex-col">
        <DrawerReferences subsidy={subsidy} search={search} setSearch={setSearch} />

        {cards.length > 0 && (
          <section className="mt-5">
            <SectionHeading>Limites par période</SectionHeading>
            <LimitCardGrid subsidy={subsidy} cards={cards} />
          </section>
        )}

        <section className="mt-5">
          <SectionHeading>Où l'utiliser</SectionHeading>
          <UniversePills subsidy={subsidy} />
        </section>

        {comments.length > 0 && (
          <section className="mt-5">
            <SectionHeading>Note du CSE</SectionHeading>
            <div className="rounded-xl border border-primary/15 bg-lightprimary p-3.5">
              {comments.map((comment) => (
                <p key={comment} className="whitespace-pre-line text-[12.5px] leading-relaxed text-gray-600 dark:text-gray-300">
                  {comment}
                </p>
              ))}
            </div>
          </section>
        )}

        <SubventionUsageHistory subsidy={subsidy} holderId={holderId} open={open} />
      </div>
    </ResponsiveDialog>
  );
};

SubventionDetailDrawer.displayName = "SubventionDetailDrawer";
